import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthGuard } from "../App";
import { RoleGuard } from "../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faTools, faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons"; // Added relevant icons
import { StandardURL } from "../App";


const VacatingPage = () => {
  const navigate = useNavigate();
  const [vacatingHouses, setVacatingHouses] = useState([]);

  useEffect(() => {
    // Retrieve propertyId and token from local storage
    const propertyId = localStorage.getItem("selectedPropertyId");
    const token = localStorage.getItem("token");

    // Fetch vacating houses using axios
    axios
      .get(
        `${StandardURL}/iNyumba/vacatinghouse/properties/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        setVacatingHouses(response.data.response);
      })
      .catch((error) => {
        console.error("Error fetching vacating houses:", error);
      });
  }, []);

  const handleCompleteVacating = (vacatingId) => {
    localStorage.setItem("selectedVacatingId", vacatingId);
    navigate(`/landlord/updatevacating`);
  };

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-4xl mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-200">
            {/* --- Header Section --- */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
              <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight flex items-center">
                Vacating Houses 🏠
              </h1>
            </div>

            <p className="text-gray-600 mb-8 max-w-3xl mx-auto">
              Monitor and manage the status of houses in the vacating process, including repair details and completion.
            </p>

            {/* --- Vacating Houses List --- */}
            <div className="space-y-6">
              {vacatingHouses.length > 0 ? (
                vacatingHouses.map((house) => (
                  <div
                    key={house.id}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-200 ease-in-out"
                  >
                    <div className="flex justify-between items-center mb-4 border-b pb-3">
                      <h2 className="text-2xl font-bold text-indigo-700 flex items-center">
                        <FontAwesomeIcon icon={faHome} className="mr-3 text-indigo-500" />
                        House {house.house_number}
                      </h2>
                      <span
                        className={`px-4 py-1 rounded-full text-sm font-semibold
                          ${house.vacatingstatus === "Pending" ? "bg-yellow-100 text-yellow-800" :
                            house.vacatingstatus === "Completed" ? "bg-green-100 text-green-800" :
                            "bg-gray-100 text-gray-800"
                          }`}
                      >
                        Status: {house.vacatingstatus}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-gray-600 text-sm font-medium mb-1">
                          <FontAwesomeIcon icon={faTools} className="mr-2 text-gray-500" />
                          Repair Description & Quotations:
                        </p>
                        <p className="text-gray-800 bg-gray-100 p-3 rounded-md border border-gray-200 text-sm">
                          {house.repaircharges || "No description provided."}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm font-medium mb-1">
                          Total Amount for Repairs:
                        </p>
                        <p className="text-gray-800 bg-gray-100 p-3 rounded-md border border-gray-200 text-lg font-semibold text-right">
                          Ksh{" "}
                          {parseFloat(house.totalamountrepairs || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between items-center mt-6 pt-4 border-t">
                      <p className="text-gray-700 font-medium flex items-center">
                        <span className="mr-2">Completion Status:</span>
                        {house.completestatus === "Complete" ? (
                          <span className="text-green-600 font-semibold flex items-center">
                            <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                            Complete
                          </span>
                        ) : (
                          <span className="text-red-600 font-semibold flex items-center">
                            <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
                            Pending
                          </span>
                        )}
                      </p>
                      {house.completestatus !== "Complete" && ( // Only show button if not already complete
                        <button
                          onClick={() => handleCompleteVacating(house.vacatinghouse_id)}
                          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-5 rounded-lg transition-colors duration-200 ease-in-out transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                          Mark as Complete
                        </button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white p-8 rounded-lg shadow-md text-center text-lg text-gray-500 italic">
                  <div className="flex flex-col items-center justify-center">
                    <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m7 0V5a2 2 0 012-2h2a2 2 0 012 2v6m-6 0h6"></path></svg>
                    No houses currently in the vacating process.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default VacatingPage;
